/**
 * GameModal Component
 *
 * Unified modal that combines schedule browsing, box scores, and lineup/roster intel
 * into a single tabbed experience. Replaces the separate BoxScoreModal and RosterIntelPanel
 * modals with a single entry point.
 *
 * Modes:
 * - Schedule mode (no game selected): renders GamePicker with dual-action buttons
 * - Game detail mode (game selected): header with back button, two tabs (Box Score | Lineup)
 */

import { useCurrentLeagueKey } from '@hooks/useCurrentLeagueKey';
import type { GameIdSource } from '@parlay-picker/shared/hooks';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { hasPlayerProps } from '@parlay-picker/shared/league';
import {
  findTeam,
  getTeamLogo,
  getTeamsForLeague,
  type TeamInfo,
} from '@parlay-picker/shared/teams';
import type { GameModalState, GameModalTab } from '@stores/globalShortcutsStore';
import { ArrowLeft, X as XIcon } from 'lucide-react';
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BoxScoreView } from './BoxScoreView';
import type { GamePickerGame } from './GamePicker';
import { GamePicker } from './GamePicker';
import { LineupView } from './LineupView';
import { TeamLogo } from './TeamLogo';

// =============================================================================
// Types
// =============================================================================

interface GameModalProps {
  state: GameModalState;
  onClose: () => void;
}

// =============================================================================
// Component
// =============================================================================

export function GameModal({ state, onClose }: GameModalProps): ReactElement {
  // Selected game — null means schedule/picker mode
  const [selectedGame, setSelectedGame] = useState(state.game);
  const [gameIdSource, setGameIdSource] = useState<GameIdSource>(
    state.game?.gameId ? 'odds_api' : 'bdl',
  );
  const [activeTab, setActiveTab] = useState<GameModalTab>(state.defaultTab);
  const [highlightedPlayers, setHighlightedPlayers] = useState(state.highlightedPlayers);

  // Lineup-specific state
  const [teamAbbr, setTeamAbbr] = useState(state.teamAbbr);
  const [opponentAbbr, setOpponentAbbr] = useState(state.opponentAbbr);

  // Sync when modal state changes externally (e.g., re-opened with different game)
  useEffect(() => {
    setSelectedGame(state.game);
    setGameIdSource(state.game?.gameId ? 'odds_api' : 'bdl');
    setActiveTab(state.defaultTab);
    setHighlightedPlayers(state.highlightedPlayers);
    setTeamAbbr(state.teamAbbr);
    setOpponentAbbr(state.opponentAbbr);
  }, [state]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const isPickerMode = !selectedGame;

  // Handle game selection from picker
  const handleSelectGame = useCallback((game: GamePickerGame, tab: GameModalTab) => {
    setSelectedGame({
      gameId: game.id,
      homeTeam: game.homeTeam,
      awayTeam: game.awayTeam,
      leagueKey: game.leagueKey,
      gameStartsAt: game.commenceTime,
    });
    setGameIdSource('bdl');
    setActiveTab(tab);
    setHighlightedPlayers([]);
    // Always derive team abbreviations so switching tabs works
    const homeInfo = findTeam(game.homeTeam, game.leagueKey);
    const awayInfo = findTeam(game.awayTeam, game.leagueKey);
    setTeamAbbr(awayInfo?.abbr ?? game.awayTeam);
    setOpponentAbbr(homeInfo?.abbr ?? game.homeTeam);
  }, []);

  // Go back to schedule view
  const handleBack = useCallback(() => {
    setSelectedGame(null);
    setGameIdSource('odds_api');
    setTeamAbbr(undefined);
    setOpponentAbbr(undefined);
  }, []);

  // Build header title for game detail mode
  const gameTitle = selectedGame
    ? `${selectedGame.awayTeam ?? '?'} @ ${selectedGame.homeTeam ?? '?'}`
    : '';

  // A selected game carries its league; with none, browse the league the user is on.
  const currentLeagueKey = useCurrentLeagueKey();
  const leagueKey: LeagueKey = selectedGame?.leagueKey ?? currentLeagueKey;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/80 flex items-start justify-center overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col bg-[var(--color-surface)] rounded-xl my-4 mx-4 md:my-8 shadow-2xl border border-theme-border/50 animate-in fade-in slide-in-from-bottom-4 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-theme-border/50">
          <div className="flex items-center gap-2 min-w-0">
            {!isPickerMode && (
              <button
                type="button"
                onClick={handleBack}
                className="p-1 rounded-lg hover:bg-theme-surface-hover transition-colors text-theme-text-secondary hover:text-white shrink-0"
                title="All games"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            {isPickerMode && activeTab === 'lineup' && teamAbbr ? (
              <h2 className="text-sm font-semibold text-white">Roster Intel</h2>
            ) : isPickerMode ? (
              <h2 className="text-sm font-semibold text-white">Today&apos;s Games</h2>
            ) : (
              <div className="flex items-center gap-2 min-w-0">
                {selectedGame?.awayTeam && (
                  <TeamLogo name={selectedGame.awayTeam} leagueKey={leagueKey} size="sm" />
                )}
                <h2 className="text-sm font-semibold text-white truncate">{gameTitle}</h2>
                {selectedGame?.homeTeam && (
                  <TeamLogo name={selectedGame.homeTeam} leagueKey={leagueKey} size="sm" />
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Tabs — only shown in game detail mode */}
            {!isPickerMode && (
              <div className="flex items-center bg-theme-surface-hover/50 rounded-lg p-0.5">
                <button
                  type="button"
                  onClick={() => setActiveTab('boxScore')}
                  className={`text-xs px-3 py-1 rounded-md transition-colors ${
                    activeTab === 'boxScore'
                      ? 'bg-theme-primary-fill text-theme-on-primary-fill font-medium'
                      : 'text-theme-text-secondary hover:text-white'
                  }`}
                >
                  Box Score
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('lineup')}
                  className={`text-xs px-3 py-1 rounded-md transition-colors ${
                    activeTab === 'lineup'
                      ? 'bg-theme-primary-fill text-theme-on-primary-fill font-medium'
                      : 'text-theme-text-secondary hover:text-white'
                  }`}
                >
                  Lineup
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-theme-surface-hover transition-colors text-theme-text-secondary hover:text-white"
            >
              <XIcon size={16} />
            </button>
          </div>
        </div>

        {/* Team toggle for lineup tab (away/home) */}
        {!isPickerMode && activeTab === 'lineup' && teamAbbr && opponentAbbr && (
          <div className="flex items-center gap-1 px-4 py-2 border-b border-theme-border/30">
            {[teamAbbr, opponentAbbr].map((abbr) => (
              <button
                key={abbr}
                type="button"
                onClick={() => {
                  // Swap: the clicked team becomes primary, the other becomes opponent
                  if (abbr !== teamAbbr) {
                    setTeamAbbr(abbr);
                    setOpponentAbbr(teamAbbr);
                  }
                }}
                className={`text-xs px-2.5 py-1 rounded-full border transition ${
                  teamAbbr === abbr
                    ? 'bg-theme-primary-fill border-theme-primary text-theme-on-primary-fill'
                    : 'border-theme-border text-theme-text-secondary hover:text-theme-text'
                }`}
              >
                {abbr}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="p-3 overflow-y-auto flex-1 min-h-0">
          {isPickerMode && !(activeTab === 'lineup' && teamAbbr) ? (
            <ScheduleView leagueKey={leagueKey} onSelectGame={handleSelectGame} />
          ) : activeTab === 'boxScore' && selectedGame ? (
            <BoxScoreView
              gameId={selectedGame.gameId}
              gameIdSource={gameIdSource}
              leagueKey={leagueKey}
              highlightedPlayers={highlightedPlayers}
              homeTeam={selectedGame.homeTeam}
              awayTeam={selectedGame.awayTeam}
              gameStartsAt={selectedGame.gameStartsAt}
            />
          ) : /* Lineup tab */
          !hasPlayerProps(leagueKey) ? (
            <div className="p-8 text-center space-y-2">
              <p className="text-sm text-theme-text-secondary">
                Roster intel is available for leagues with player props only
              </p>
              <p className="text-xs text-theme-text-tertiary">
                College roster data is not currently supported
              </p>
            </div>
          ) : teamAbbr ? (
            <LineupView teamAbbr={teamAbbr} opponentAbbr={opponentAbbr} leagueKey={leagueKey} />
          ) : (
            <div className="p-8 text-center text-sm text-theme-text-secondary">
              Select a team from the schedule to view lineup intel
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Schedule View — tabs: Schedule | All Teams
// =============================================================================

type ScheduleTab = 'schedule' | 'allTeams';
type BrowseStep = 'grid' | 'opponent';

function ScheduleView({
  leagueKey,
  onSelectGame,
}: {
  leagueKey: LeagueKey;
  onSelectGame: (game: GamePickerGame, tab: GameModalTab) => void;
}) {
  const [scheduleTab, setScheduleTab] = useState<ScheduleTab>('schedule');
  const [browseStep, setBrowseStep] = useState<BrowseStep>('grid');
  const [browseTeam, setBrowseTeam] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [opponentQuery, setOpponentQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Team list for grid: a league with no team map has no teams to list
  const allTeams: (TeamInfo & { name: string })[] = useMemo(
    () =>
      Object.entries(getTeamsForLeague(leagueKey) ?? {}).map(([name, info]) => ({
        ...info,
        name,
      })),
    [leagueKey],
  );

  const filteredTeams = useMemo(
    () =>
      searchQuery
        ? allTeams.filter(
            (t) =>
              t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              t.abbr.toLowerCase().includes(searchQuery.toLowerCase()),
          )
        : allTeams,
    [allTeams, searchQuery],
  );

  const filteredOpponents = useMemo(
    () =>
      opponentQuery
        ? allTeams.filter(
            (t) =>
              t.abbr !== browseTeam &&
              (t.name.toLowerCase().includes(opponentQuery.toLowerCase()) ||
                t.abbr.toLowerCase().includes(opponentQuery.toLowerCase())),
          )
        : allTeams.filter((t) => t.abbr !== browseTeam),
    [allTeams, opponentQuery, browseTeam],
  );

  const handlePickTeam = (abbr: string) => {
    setBrowseTeam(abbr);
    setBrowseStep('opponent');
    setSearchQuery('');
    setOpponentQuery('');
  };

  const handlePickOpponent = (opponentAbbr: string | null) => {
    if (!browseTeam) return;
    // Build a synthetic game for the lineup view
    const teamInfo = findTeam(browseTeam, leagueKey);
    const oppInfo = opponentAbbr ? findTeam(opponentAbbr, leagueKey) : null;
    const syntheticGame: GamePickerGame = {
      id: `browse-${browseTeam}-${opponentAbbr ?? 'solo'}`,
      homeTeam: teamInfo?.name ?? browseTeam,
      awayTeam: oppInfo?.name ?? opponentAbbr ?? '',
      commenceTime: '',
      leagueKey,
      gameStatus: 'pre-game',
      homeScore: null,
      awayScore: null,
      quarter: null,
      timeRemaining: null,
    };
    onSelectGame(syntheticGame, 'lineup');
  };

  const handleBackToGrid = () => {
    setBrowseStep('grid');
    setBrowseTeam(null);
    setOpponentQuery('');
  };

  return (
    <div>
      {/* Schedule | All Teams toggle */}
      <div className="flex items-center gap-1 mb-3">
        {(['schedule', 'allTeams'] as const).map((tab) => (
          <button
            type="button"
            key={tab}
            onClick={() => {
              setScheduleTab(tab);
              setBrowseStep('grid');
              setBrowseTeam(null);
              setSearchQuery('');
            }}
            className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
              scheduleTab === tab
                ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                : 'text-theme-text-tertiary hover:text-theme-text-secondary hover:bg-theme-surface-hover'
            }`}
          >
            {tab === 'schedule' ? 'Schedule' : 'All Teams'}
          </button>
        ))}
      </div>

      {scheduleTab === 'schedule' ? (
        <GamePicker
          leagueKey={leagueKey}
          onSelectBoxScore={(game) => onSelectGame(game, 'boxScore')}
          onSelectLineup={(game) => onSelectGame(game, 'lineup')}
        />
      ) : browseStep === 'grid' ? (
        /* Team Grid */
        <div className="space-y-3">
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search teams..."
            className="w-full px-3 py-2 text-sm rounded-lg bg-theme-surface-hover border border-theme-border text-theme-text placeholder:text-theme-text-tertiary focus:outline-none focus:border-theme-primary"
          />
          <div className="grid grid-cols-5 md:grid-cols-6 gap-2">
            {filteredTeams.map((team) => (
              <button
                type="button"
                key={team.abbr}
                onClick={() => handlePickTeam(team.abbr)}
                className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-theme-surface-hover transition group"
                title={team.name}
              >
                <img
                  src={team.logo}
                  alt={team.abbr}
                  loading="lazy"
                  className="w-8 h-8 object-contain"
                />
                <span className="text-xs text-theme-text-tertiary group-hover:text-theme-text font-mono">
                  {team.abbr}
                </span>
              </button>
            ))}
            {filteredTeams.length === 0 && (
              <div className="col-span-full text-xs text-theme-text-tertiary text-center py-4">
                No teams found
              </div>
            )}
          </div>
        </div>
      ) : (
        /* H2H Opponent Selector */
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleBackToGrid}
              className="p-1 rounded-lg hover:bg-theme-surface-hover transition text-theme-text-secondary"
            >
              <ArrowLeft size={16} />
            </button>
            {browseTeam && (
              <div className="flex items-center gap-2">
                {getTeamLogo(browseTeam, leagueKey) && (
                  <img
                    src={getTeamLogo(browseTeam, leagueKey)!}
                    alt={browseTeam}
                    className="w-6 h-6 object-contain"
                  />
                )}
                <span className="text-sm font-medium text-white">
                  {findTeam(browseTeam, leagueKey)?.name ?? browseTeam}
                </span>
              </div>
            )}
            <span className="text-xs text-theme-text-tertiary">— select opponent</span>
          </div>

          {/* No Opponent option */}
          <button
            type="button"
            onClick={() => handlePickOpponent(null)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-theme-border/50 hover:bg-theme-surface-hover transition text-left"
          >
            <div className="w-6 h-6 rounded-full bg-theme-surface-hover flex items-center justify-center text-xs text-theme-text-tertiary">
              —
            </div>
            <span className="text-sm text-theme-text-secondary">No Opponent (roster only)</span>
          </button>

          {/* Opponent search */}
          <input
            type="text"
            value={opponentQuery}
            onChange={(e) => setOpponentQuery(e.target.value)}
            placeholder="Search opponent..."
            className="w-full px-3 py-2 text-sm rounded-lg bg-theme-surface-hover border border-theme-border text-theme-text placeholder:text-theme-text-tertiary focus:outline-none focus:border-theme-primary"
          />

          {/* Opponent grid */}
          <div className="grid grid-cols-5 md:grid-cols-6 gap-2">
            {filteredOpponents.map((team) => (
              <button
                type="button"
                key={team.abbr}
                onClick={() => handlePickOpponent(team.abbr)}
                className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-theme-surface-hover transition group"
                title={team.name}
              >
                <img
                  src={team.logo}
                  alt={team.abbr}
                  loading="lazy"
                  className="w-8 h-8 object-contain"
                />
                <span className="text-xs text-theme-text-tertiary group-hover:text-theme-text font-mono">
                  {team.abbr}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default GameModal;
